import type { TypeChatChannel, TypeChatNotifyPrefs } from '@/types/conversation/types.conversation.chat.channel';
import type { TypeChatMessage } from '@/types/conversation/types.conversation.chat.message';

type TypeChatNotifyDecision = { toast: boolean; desktop: boolean; sound: boolean; reason: string };

type Input = {
    message: TypeChatMessage;
    channel: TypeChatChannel | null;
    me: string | null;
    prefs: TypeChatNotifyPrefs;
    activeChannelId: string | null;
    visible: boolean;
    desktopAllowed: boolean;
    seen: boolean;
    now?: number;
};

const FRESH_MS = 2 * 60 * 1000;

const none = (reason: string): TypeChatNotifyDecision => ({ toast: false, desktop: false, sound: false, reason });

export function chatNotifyDecide({ message, channel, me, prefs, activeChannelId, visible, desktopAllowed, seen, now = Date.now() }: Input): TypeChatNotifyDecision {
    if (!channel || !me) return none('unknown');
    if (seen || message.pending || message.kind === 'event' || message.editedAt) return none('not-new');
    if (message.authorId === me) return none('mine');
    const at = new Date(message.at).getTime();
    if (!Number.isFinite(at) || now - at > FRESH_MS) return none('old');
    if (prefs.level === 'none') return none('off');
    const mention = message.mentions.includes(me);
    const muted = Boolean(channel.mutedUntil && new Date(channel.mutedUntil).getTime() > now);
    if (muted && !mention) return none('muted');
    if (channel.archivedAt && !mention) return none('archived');
    if (prefs.level === 'important' && channel.kind === 'group' && !mention) return none('not-important');
    if (visible && activeChannelId === channel.id) return none('looking');
    const desktop = !visible && prefs.desktop && desktopAllowed;
    const toast = visible;
    return { toast, desktop, sound: prefs.sound && (toast || desktop), reason: mention ? 'mention' : 'message' };
}

export default chatNotifyDecide;
